/* Analýza V2+ — HORIZONT CÍLE, když ho nikdo nezadal (P0-4).
 *
 * PROČ (nález UAT):
 * Cíl bez vyplněného roku se na Cestě života vykreslil jako „DNES" a dostal
 * modrý chip „Krátkodobý". Důvod je banální a zákeřný: +"" === 0 a
 * Number(null) === 0, takže se z CHYBĚJÍCÍHO termínu stal termín „letos".
 * Poradce pak klientovi ukázal cíl, který má být hotový hned — a nikdo ho
 * takhle nezadal.
 *
 * Pravidlo: nezadaný horizont se pojmenuje, nedopočítává se.
 *   roky = 0            → DNES (povinná likvidní rezerva ho má schválně)
 *   roky = null/""/NaN  → „Termín zatím není stanovený"
 *
 * Stejnou logiku má CilCinema (roky == null → „bez termínu"); tady je vytažená
 * do čisté funkce, aby ji Cesta života i řádek cíle používaly společně.
 */

import { textJazyka } from "@/lib/i18n/textMimoReact";

export const BEZ_TERMINU = {
  cs: "Termín zatím není stanovený",
  sk: "Termín zatiaľ nie je stanovený",
};

/** Je horizont opravdu zadaný? Prázdný string ani null se NEPOČÍTÁ jako 0. */
export function horizontZadan(roky) {
  if (roky === null || roky === undefined) return false;
  if (typeof roky === "string" && roky.trim() === "") return false;
  return Number.isFinite(Number(roky));
}

/** Text horizontu do karty cíle. Nezadaný termín se pojmenuje, ne vymyslí. */
/* Texty ze slovníku (pět jazyků) — karta cíle stojí na plátně klienta
 * a „za 25 let“ tam Vietnamec číst nemá. Česky a slovensky beze změny. */
export function horizontText(roky, lang = "cs") {
  if (!horizontZadan(roky)) return textJazyka(lang, "analyzaPrezentace.horizont.bezTerminu");
  const r = Number(roky);
  if (r === 0) return textJazyka(lang, "analyzaProdej.cesta.dnes");
  return textJazyka(lang, "analyzaProdej.cesta.za", undefined, { n: r });
}

/**
 * Věk klienta v době cíle. Bez známého horizontu NEBO bez data narození
 * vrací null — „ve věku 41" u cíle bez termínu je vymyšlený údaj.
 */
export function vekVCili(vekDnes, roky) {
  if (vekDnes == null || !Number.isFinite(Number(vekDnes))) return null;
  if (!horizontZadan(roky)) return null;
  return Number(vekDnes) + Number(roky);
}

/* ═══ ČÍSELNÝ HORIZONT — JEDEN DOPOČET PRO CELOU APPKU ════════════════════
 *
 * PROČ (nález auditu R13, 25. 8. 2026):
 * Týž cíl měl v aplikaci PĚT různých horizontů. Rezerva „hned" (roky = 0) se
 * na kartě v Návrhu ukázala jako „za 1 let", v tisku jako „—", na slajdu jako
 * „za 0 let" a v analýze jako „DNES". Důvod: každá vrstva si horizont
 * dopočítávala sama — `Math.max(1, +g.roky || 0)` tady, `g.roky ? … : "—"`
 * tam. Osm míst, osm pravidel.
 *
 * Rozdíl, na kterém to celé stojí a který se NESMÍ slít:
 *   roky = 0    → „HNED". Klientův údaj. Počítá se.
 *   roky = null → „poradce nezadal". NEPOČÍTÁ se a NETVRDÍ se procento.
 * `+"" === 0` a `Number(null) === 0` znamenají, že se ty dva stavy slijí samy
 * od sebe, kdykoli si je někdo dopočítá znovu. Proto tahle funkce.
 *
 * `H` je horizont pro PROJEKCI (glide path, úročení) — tam nula smysl nedává,
 * takže je to `max(1, roky)`. `roky` je to, co se ukazuje klientovi. Dvě různé
 * veličiny, schválně dvě různá pole: kdo kreslí graf, bere `H`; kdo píše větu,
 * bere `roky` (a `dnes`).
 */

/** Strop horizontu. Delší než lidský život neplánujeme (zrcadlí planEngine). */
export const ROKY_STROP = 80;

/**
 * Číselný horizont cíle — jediný zdroj pravdy pro zobrazení i pro projekci.
 * @param {*} roky hodnota z analýzy nebo z přepisu poradce
 * @returns {{roky: number|null, zadano: boolean, dnes: boolean, H: number}}
 */
export function horizontCisla(roky) {
  /* Záporný horizont je rozbitý vstup, ne „hned" — stejné pravidlo jako
   * v planEngine (`horizont()`) i v planSolveru (`rokyZadane`). */
  if (!horizontZadan(roky) || Number(roky) < 0) return { roky: null, zadano: false, dnes: false, H: 1 };
  const r = Math.min(Math.round(Number(roky)), ROKY_STROP);
  return { roky: r, zadano: true, dnes: r === 0, H: Math.max(1, r) };
}

/* Kategorie plurálu, když překladač NEMÁ `t.plural` (testovací harness, starší
 * kontext). České pravidlo 1 / 2–4 / 5+ je horší než Intl, ale pořád nekonečně
 * lepší než pád obrazovky uprostřed schůzky s klientem. */
const KATEGORIE_ZALOHA = (n) => (n === 1 ? "One" : n >= 2 && n <= 4 ? "Few" : "Many");

/**
 * Věta o horizontu ve VŠECH PĚTI jazycích — „hned" / „za 1 rok" / „za 5 let".
 *
 * Skloňování dělá `t.plural` (Intl.PluralRules podle jazyka), ne české
 * pravidlo 1 / 2–4 / 5+ — jinak by Ukrajinec četl „через 21 років" místo
 * „через 21 рік". Text bez zadaného horizontu se POJMENUJE, nevymýšlí.
 *
 * @param {*} roky
 * @param {function} t překladač z LanguageContext (ideálně s `t.plural`)
 * @returns {string}
 */
export function textHorizontu(roky, t) {
  const h = horizontCisla(roky);
  if (!h.zadano) return t("cilHorizont.bezTerminu");
  if (h.dnes) return t("cilHorizont.hned");
  if (typeof t.plural === "function") return t.plural("cilHorizont.za", h.roky);
  return String(t(`cilHorizont.za${KATEGORIE_ZALOHA(h.roky)}`)).replace("{n}", String(h.roky));
}

/** Holá jednotka („5 let", „21 рік") — do tabulek, kde předložka nepatří. */
export function jednotkaLet(roky, t) {
  const h = horizontCisla(roky);
  if (!h.zadano) return t("cilHorizont.bezTerminu");
  if (h.dnes) return t("cilHorizont.hned");
  const jednotka = typeof t.plural === "function"
    ? t.plural("cilHorizont.rok", h.roky)
    : t(`cilHorizont.rok${KATEGORIE_ZALOHA(h.roky)}`);
  return `${h.roky} ${jednotka}`;
}
